import { DatePipe, NgClass, SlicePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, of, Subject, takeUntil, tap } from 'rxjs';

import { ContactService } from 'src/app/core/services/contact';
import { User } from 'src/app/models/user.model';
import { ToastNotificationComponent } from '../../shared/components/toast-notification/toast-notification.component';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from '../../shared/utils/dropdown.util';
import * as fromStore from '../../store';
import { OktaUserService } from '../services/okta-user';
import type {
  AssignToGroupResponse,
  FederationCheckData,
  MakeFederatedResponse,
  OktaUserExistsData,
  OktaUserListItem,
  OktaUserRequest,
  OktaUserResponse,
  OktaUserStatus,
} from '../services/okta-user/okta-user.types';

interface StatusConfig {
  label: string;
  classes: string;
  dot: string;
}

const STATUS_CONFIG: Record<OktaUserStatus, StatusConfig> = {
  ACTIVE: {
    label: 'Active',
    classes: 'bg-green-100 text-green-700',
    dot: 'bg-green-500',
  },
  LOCKED_OUT: {
    label: 'Locked Out',
    classes: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
  },
  SUSPENDED: {
    label: 'Suspended',
    classes: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
  },
  PROVISIONED: {
    label: 'Provisioned',
    classes: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-700',
  },
  STAGED: {
    label: 'Staged',
    classes: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
  },
  RECOVERY: {
    label: 'Recovery',
    classes: 'bg-purple-100 text-purple-700',
    dot: 'bg-purple-300',
  },
  PASSWORD_EXPIRED: {
    label: 'Password Expired',
    classes: 'bg-orange-100 text-orange-700',
    dot: 'bg-orange-500',
  },
  DEPROVISIONED: {
    label: 'Deprovisioned',
    classes: 'bg-gray-100 text-gray-500',
    dot: 'bg-gray-300',
  },
};

@Component({
  selector: 'app-okta-admin',
  templateUrl: 'okta-admin.component.html',
  imports: [
    DatePipe,
    FormsModule,
    MatIconModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    NgClass,
    ReactiveFormsModule,
    SlicePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OktaAdminComponent implements OnInit, AfterViewInit, OnDestroy {
  // --- Form ---
  private readonly fb = inject(FormBuilder);

  private readonly contactService = inject(ContactService);

  readonly onboardingForm = this.fb.group({
    username: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
  });

  readonly isSubmitting = signal(false);

  readonly formError = signal<string | null>(null);

  readonly formSuccess = signal<string | null>(null);

  // --- Federation check form ---
  readonly federationForm = this.fb.group({
    username: ['', Validators.required],
  });

  readonly isChecking = signal(false);

  readonly federationResult = signal<FederationCheckData | null>(null);

  readonly federationError = signal<string | null>(null);

  readonly existsResult = signal<OktaUserExistsData | null>(null);

  readonly existsError = signal<string | null>(null);

  // --- Assign to group ---
  readonly isAssigningToGroup = signal(false);

  readonly assignToGroupResult = signal<AssignToGroupResponse | null>(null);

  readonly assignToGroupError = signal<string | null>(null);

  // --- Make federated form ---
  readonly makeFederatedForm = this.fb.group({
    username: ['', Validators.required],
  });

  readonly isMakingFederated = signal(false);

  readonly makeFederatedResult = signal<MakeFederatedResponse | null>(null);

  readonly makeFederatedError = signal<string | null>(null);

  // --- Delete user ---
  readonly isDeleting = signal(false);

  readonly deleteConfirmPending = signal(false);

  readonly deleteError = signal<string | null>(null);

  readonly deleteSuccess = signal<string | null>(null);

  private readonly store = inject(Store<fromStore.State>);

  readonly loggedInUser = signal<User>({} as User);

  // --- Table ---
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private readonly oktaUserService = inject(OktaUserService);

  private readonly snackBar = inject(MatSnackBar);

  private readonly elementRef = inject(ElementRef);

  private readonly destroy$ = new Subject<void>();

  readonly isLoading = signal(true);

  readonly errorMessage = signal<string | null>(null);

  readonly includeMfa = signal(false);

  readonly statusConfig = STATUS_CONFIG;

  readonly statusOptions = Object.entries(STATUS_CONFIG) as [
    OktaUserStatus,
    StatusConfig,
  ][];

  readonly dataSource = new MatTableDataSource<OktaUserListItem>([]);

  readonly displayedColumns = [
    'avatar',
    'user',
    'email',
    'login',
    'status',
    'authProvider',
    'created',
    'lastLogin',
    'statusChanged',
    'passwordChanged',
    'mfa',
    'actions',
  ];

  readonly pageSizeOptions = [10, 25, 50];

  readonly defaultPageSize = 25;

  searchTerm = '';

  statusFilter: OktaUserStatus[] = [];

  authProviderFilter = signal<'OKTA' | 'FEDERATION' | ''>('');

  isStatusFilterOpen = signal(false);

  searchField: 'all' | 'login' | 'email' | 'name' = 'all';

  ngOnInit(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const { search, statuses, field, authProvider } = JSON.parse(filter) as {
        search: string;
        statuses: OktaUserStatus[];
        field: 'all' | 'login' | 'email' | 'name';
        authProvider: 'OKTA' | 'FEDERATION' | '';
      };
      const term = search.toLowerCase();
      let matchesSearch = true;
      if (term) {
        switch (field) {
          case 'login':
            matchesSearch = !!data.login?.toLowerCase().includes(term);
            break;
          case 'email':
            matchesSearch = !!data.email?.toLowerCase().includes(term);
            break;
          case 'name':
            matchesSearch =
              !!data.firstName?.toLowerCase().includes(term) ||
              !!data.lastName?.toLowerCase().includes(term);
            break;
          default:
            matchesSearch =
              !!data.login?.toLowerCase().includes(term) ||
              !!data.firstName?.toLowerCase().includes(term) ||
              !!data.lastName?.toLowerCase().includes(term) ||
              !!data.email?.toLowerCase().includes(term) ||
              !!data.loginIssue?.reason?.toLowerCase().includes(term);
        }
      }
      const matchesStatus = !statuses.length || statuses.includes(data.status);
      const matchesAuth = !authProvider || data.authProvider === authProvider;
      return matchesSearch && matchesStatus && matchesAuth;
    };

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => this.loggedInUser.set(user));

    this.loadUsers();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (user, column) => {
      switch (column) {
        case 'user':
          return `${user.lastName} ${user.firstName}`.toLowerCase();
        case 'lastLogin':
          return user.lastLogin ?? '';
        case 'statusChanged':
          return user.statusChanged ?? '';
        case 'passwordChanged':
          return user.passwordChanged ?? '';
        case 'mfa':
          if (!user.mfa) return '2';
          if (!user.mfa.enrolled) return '1';
          return `0 ${user.mfa.factors
            ?.map((factor) => `${factor.provider} ${factor.type}`)
            .join(' ')}`;
        default:
          return (
            ((user as unknown as Record<string, unknown>)[column] as string) ??
            ''
          );
      }
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(
      this.elementRef,
      event,
      this.isStatusFilterOpen,
    );
  }

  // --- Form actions ---

  onAssignToGroup(): void {
    const username = this.federationResult()?.username;
    if (!username || this.isAssigningToGroup()) return;

    this.isAssigningToGroup.set(true);
    this.assignToGroupResult.set(null);
    this.assignToGroupError.set(null);

    this.oktaUserService
      .assignToGroup(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.assignToGroupResult.set(res);
          this.isAssigningToGroup.set(false);
          this.onLookUpUser();
        },
        error: (err) => {
          this.assignToGroupError.set(
            err?.message || 'Failed to assign user to group.',
          );
          this.isAssigningToGroup.set(false);
        },
      });
  }

  onLookUpUser(): void {
    const username = this.federationForm.value.username?.trim().toLowerCase();
    if (!username || this.isChecking()) return;

    this.isChecking.set(true);
    this.federationResult.set(null);
    this.federationError.set(null);
    this.existsResult.set(null);
    this.existsError.set(null);

    forkJoin({
      federation: this.oktaUserService.checkFederation(username).pipe(
        tap({
          error: (err) =>
            this.federationError.set(
              err?.message || 'Failed to check federation status.',
            ),
        }),
        catchError(() => of(null)),
      ),
      exists: this.oktaUserService.checkExists(username).pipe(
        tap({
          error: (err) =>
            this.existsError.set(
              err?.message || 'Failed to check if user exists.',
            ),
        }),
        catchError(() => of(null)),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ federation, exists }) => {
          if (federation) this.federationResult.set(federation.data);
          if (exists) this.existsResult.set(exists.data);
          this.isChecking.set(false);
        },
        error: () => this.isChecking.set(false),
      });
  }

  onMakeFederated(): void {
    const { username } = this.makeFederatedForm.value;
    if (!username || this.isMakingFederated()) return;

    this.isMakingFederated.set(true);
    this.makeFederatedResult.set(null);
    this.makeFederatedError.set(null);

    this.oktaUserService
      .makeFederated(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.makeFederatedResult.set(res);
          this.isMakingFederated.set(false);
        },
        error: (err) => {
          this.makeFederatedError.set(
            err?.message || 'Failed to convert user to federated.',
          );
          this.isMakingFederated.set(false);
        },
      });
  }

  onDeleteUser(): void {
    this.deleteConfirmPending.set(true);
    this.deleteError.set(null);
    this.deleteSuccess.set(null);
  }

  cancelDelete(): void {
    this.deleteConfirmPending.set(false);
  }

  onConfirmDelete(): void {
    const { username } = this.makeFederatedForm.value;
    if (!username || this.isDeleting()) return;

    this.isDeleting.set(true);
    this.deleteError.set(null);

    this.oktaUserService
      .deleteUser(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deleteSuccess.set(`"${username}" has been deleted from Okta.`);
          this.isDeleting.set(false);
          this.deleteConfirmPending.set(false);
          this.makeFederatedForm.reset();
          this.loadUsers();
        },
        error: (err: { message?: string }) => {
          this.deleteError.set(err?.message || 'Failed to delete user.');
          this.isDeleting.set(false);
          this.deleteConfirmPending.set(false);
        },
      });
  }

  isFederationCheckFederated(result: FederationCheckData | null): boolean {
    return !!(result?.userFederated && result.inGroup);
  }

  getFederationCheckLabel(result: FederationCheckData): string {
    if (!result.domainFederated) return 'Local (Password-based)';
    if (!result.existsInOkta) return 'Federated Domain — No Okta Account';
    if (!result.userFederated) return 'Config Issue — Not Federated';
    if (!result.inGroup) return 'Federated — Not in Group';
    return 'Federated (SSO)';
  }

  onSubmit(): void {
    this.formError.set(null);
    this.formSuccess.set(null);
    if (this.onboardingForm.invalid || this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const email = this.onboardingForm.value.email?.trim().toLowerCase();
    const username = this.onboardingForm.value.username?.trim().toLowerCase();

    this.contactService
      .getContactByOktaUsernameAndEmail(email, username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact) => {
          if (!contact) {
            this.formError.set(
              `No Salesforce/Hyperdrive contact or Okta user found for ${email} / ${username}.`,
            );
            this.isSubmitting.set(false);
            return;
          }

          const payload: OktaUserRequest = {
            email: contact.Email,
            login: contact.OktaUsername__c,
            firstName: contact.FirstName,
            lastName: contact.LastName,
            accountId: contact.AccountId,
          };

          this.oktaUserService
            .onboardUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.formSuccess.set(
                  'User successfully created. A default Okta activation email should be received shortly.',
                );
                this.isSubmitting.set(false);
                this.onboardingForm.reset();
                this.loadUsers();
              },
              error: (err) => {
                this.isSubmitting.set(false);
                this.formError.set(`Failed to create user. ${err?.message}`);
              },
            });
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.formError.set(
            (err?.message as string) ||
              'An error occurred looking up the contact.',
          );
        },
      });
  }

  onSubmitCustomActivationEmail(): void {
    this.formError.set(null);
    this.formSuccess.set(null);
    if (this.onboardingForm.invalid || this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const email = this.onboardingForm.value.email?.trim().toLowerCase();
    const username = this.onboardingForm.value.username?.trim().toLowerCase();

    this.contactService
      .getContactByOktaUsernameAndEmail(email, username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact) => {
          if (!contact) {
            this.formError.set(
              `No Salesforce/Hyperdrive contact or Okta user found for ${email} / ${username}.`,
            );
            this.isSubmitting.set(false);
            return;
          }

          const payload: OktaUserRequest = {
            email: contact.Email,
            login: contact.OktaUsername__c,
            firstName: contact.FirstName,
            lastName: contact.LastName,
            accountId: contact.AccountId,
          };

          this.oktaUserService
            .onboardUserWithCustomActivationEmail(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.formSuccess.set(
                  this.getCustomActivationEmailSuccessMessage(res),
                );
                this.isSubmitting.set(false);
                this.onboardingForm.reset();
                this.loadUsers();
              },
              error: (err) => {
                this.isSubmitting.set(false);
                this.formError.set(`Failed to create user. ${err?.message}`);
              },
            });
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.formError.set(
            (err?.message as string) ||
              'An error occurred looking up the contact.',
          );
        },
      });
  }

  private getCustomActivationEmailSuccessMessage(
    res: OktaUserResponse,
  ): string {
    const apiMessage = res.message?.trim();
    const data = res.status === 'ok' ? res.data : undefined;
    const isFederated = this.isFederatedOnboardingResponse(data);
    const emailWasNotSent = data?.customActivationEmailSent === false;
    const userWasAddedToGroup = res.code.toUpperCase().includes('GROUP');

    if (apiMessage && (isFederated || emailWasNotSent || userWasAddedToGroup)) {
      return apiMessage;
    }

    return 'User successfully created. A custom activation email should be received shortly.';
  }

  private isFederatedOnboardingResponse(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;

    const { authProvider } = data as { authProvider?: unknown };

    return (
      typeof authProvider === 'string' &&
      authProvider.toUpperCase().includes('FEDERATION')
    );
  }

  // --- Table filter ---

  applyFilter(): void {
    this.dataSource.filter = JSON.stringify({
      search: this.searchTerm.trim(),
      statuses: this.statusFilter,
      field: this.searchField,
      authProvider: this.authProviderFilter(),
    });
    this.dataSource.paginator?.firstPage();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilter();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = [];
    this.searchField = 'all';
    this.authProviderFilter.set('');
    this.applyFilter();
  }

  toggleStatusFilterDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isStatusFilterOpen);
  }

  toggleStatusFilter(status: OktaUserStatus, event: Event): void {
    stopEvent(event);
    this.statusFilter = this.isStatusSelected(status)
      ? this.statusFilter.filter((selectedStatus) => selectedStatus !== status)
      : [...this.statusFilter, status];
    this.applyFilter();
  }

  clearStatusFilter(event: Event): void {
    stopEvent(event);
    this.statusFilter = [];
    closeDropdown(this.isStatusFilterOpen);
    this.applyFilter();
  }

  isStatusSelected(status: OktaUserStatus): boolean {
    return this.statusFilter.includes(status);
  }

  get selectedStatusFilterLabel(): string {
    if (!this.statusFilter.length) return 'All statuses';
    if (this.statusFilter.length === 1) {
      return this.getStatusConfig(this.statusFilter[0]).label;
    }
    return `${this.statusFilter.length} statuses`;
  }

  // --- Table helpers ---

  getStatusConfig(status: OktaUserStatus): StatusConfig {
    return (
      this.statusConfig[status] ?? {
        label: status,
        classes: 'bg-gray-100 text-gray-600',
        dot: 'bg-gray-400',
      }
    );
  }

  getMfaTooltip(user: OktaUserListItem): string {
    if (!user.mfa?.factors?.length) return '';
    return user.mfa.factors
      .map((f) => `${f.provider}: ${f.type} (${f.status})`)
      .join('\n');
  }

  canSuspend(status: OktaUserStatus): boolean {
    return !['SUSPENDED', 'DEPROVISIONED'].includes(status);
  }

  canResendInvite(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      ['PROVISIONED', 'STAGED'].includes(user.status)
    );
  }

  canResetPassword(user: OktaUserListItem): boolean {
    // TODO: Restore when reset-password permission is approved.
    // return (
    //   user.authProvider !== 'FEDERATION' &&
    //   ['ACTIVE', 'PASSWORD_EXPIRED'].includes(user.status)
    // );
    return false;
  }

  canUnlock(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      ['LOCKED_OUT', 'RECOVERY'].includes(user.status)
    );
  }

  canReactivate(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      ['SUSPENDED', 'DEPROVISIONED'].includes(user.status)
    );
  }

  canResetMfa(user: OktaUserListItem): boolean {
    return (
      user.authProvider !== 'FEDERATION' &&
      !!this.getResettableMfaFactorId(user)
    );
  }

  // --- Table actions ---

  resendInvite(user: OktaUserListItem): void {
    this.oktaUserService
      .resendActivationEmail(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() =>
          this.showToast('Invitation email resent successfully.', 'success'),
        ),
        catchError((err) => {
          this.showToast(
            err?.message ?? 'Failed to resend invitation.',
            'error',
          );
          return [];
        }),
      )
      .subscribe();
  }

  // TODO: Restore when reset-password permission is approved.
  // resetPassword(user: OktaUserListItem): void {
  //   this.oktaUserService
  //     .forgotPassword(user.login)
  //     .pipe(
  //       takeUntil(this.destroy$),
  //       tap(() =>
  //         this.showToast('Password reset email sent successfully.', 'success'),
  //       ),
  //       catchError((err) => {
  //         this.showToast(
  //           err?.message ?? 'Failed to send password reset.',
  //           'error',
  //         );
  //         return [];
  //       }),
  //     )
  //     .subscribe();
  // }

  unlockUser(user: OktaUserListItem): void {
    this.oktaUserService
      .unlockUser(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('User unlocked successfully.', 'success');
          this.patchUserInTable(user.id, {
            status: 'ACTIVE',
            cannotLogin: false,
            loginIssue: undefined,
          });
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to unlock user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  resetMfa(user: OktaUserListItem): void {
    const factorId = this.getResettableMfaFactorId(user);

    if (!factorId) return;

    this.oktaUserService
      .resetMfaFactor(user.login, factorId)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('MFA factor reset successfully.', 'success');
          this.removeMfaFactorFromTable(user.id, factorId);
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to reset MFA.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  deactivateOktaUser(user: OktaUserListItem): void {
    this.oktaUserService
      .deactivateUser({ login: user.login })
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('User deactivated successfully.', 'success');
          this.patchUserInTable(user.id, {
            status: 'DEPROVISIONED',
            cannotLogin: true,
          });
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to deactivate user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  suspendUser(user: OktaUserListItem): void {
    this.oktaUserService
      .deleteUser(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast('User deleted successfully.', 'success');
          this.dataSource.data = this.dataSource.data.filter(
            (u) => u.id !== user.id,
          );
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to delete user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  reactivateUser(user: OktaUserListItem): void {
    this.oktaUserService
      .resendActivationEmail(user.login)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showToast(
            'User reactivated — activation email sent.',
            'success',
          );
          this.patchUserInTable(user.id, {
            status: 'PROVISIONED',
            cannotLogin: false,
          });
        }),
        catchError((err) => {
          this.showToast(err?.message ?? 'Failed to reactivate user.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  private patchUserInTable(id: string, patch: Partial<OktaUserListItem>): void {
    const data = [...this.dataSource.data];
    const idx = data.findIndex((u) => u.id === id);
    if (idx > -1) {
      data[idx] = { ...data[idx], ...patch };
      this.dataSource.data = data;
    }
  }

  private getResettableMfaFactorId(user: OktaUserListItem): string | null {
    return user.mfa?.enrolled
      ? (user.mfa.factors.find((factor) => !!factor.id)?.id ?? null)
      : null;
  }

  private removeMfaFactorFromTable(userId: string, factorId: string): void {
    const user = this.dataSource.data.find((u) => u.id === userId);

    if (!user?.mfa) return;

    const factors = user.mfa.factors.filter((factor) => factor.id !== factorId);

    this.patchUserInTable(userId, {
      mfa: {
        enrolled: factors.length > 0,
        factors,
      },
    });
  }

  toggleIncludeMfa(): void {
    this.includeMfa.update((v) => !v);
    this.loadUsers();
  }

  private loadUsers(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.oktaUserService
      .getUsers(this.includeMfa())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.dataSource.data = users;
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Failed to load Okta users:', err);
          this.errorMessage.set(
            err?.message || 'Failed to load users. Please try again.',
          );
          this.isLoading.set(false);
        },
      });
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(ToastNotificationComponent, {
      duration: 7000,
      panelClass: ['toast-panel'],
      data: { message, type },
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
